import { sleep } from "@/lib/utils"

import type { Api, EmailLogEntry, EmailTemplateMeta } from "../types"

// Офлайн-зеркало почтового канала: раздел «Почта» в том виде, в каком его
// увидит администратор до подключения Яндекс 360 — режим mock, журнал с
// разными исходами и список шаблонов. Предпросмотр здесь упрощённый: настоящая
// вёрстка писем собирается на шлюзе.
const now = Date.now()
const minutesAgo = (m: number) => new Date(now - m * 60_000).toISOString()

const templates: EmailTemplateMeta[] = [
	{ id: "seller_email_code", audience: "seller", category: "critical", title: "Код подтверждения почты", sensitive: true },
	{ id: "seller_order_new", audience: "seller", category: "critical", title: "Новая продажа (заказ)", sensitive: false },
	{ id: "shop_moderation_approved", audience: "seller", category: "critical", title: "Модерация магазина пройдена", sensitive: false },
	{ id: "buyer_order_confirmed", audience: "buyer", category: "critical", title: "Подтверждение заказа (состав и сумма)", sensitive: false },
	{ id: "buyer_price_drop", audience: "buyer", category: "marketing", title: "Снижение цены на избранное", sensitive: false },
]

const entry = (over: Partial<EmailLogEntry> & { id: string }): EmailLogEntry => ({
	template: "seller_order_new",
	templateTitle: "Новая продажа (заказ)",
	category: "critical",
	purpose: "order_new",
	to: "shop@example.ru",
	recipientUserId: "usr_1",
	subject: "Новый заказ №D1A3F09B на 12 800 ₽",
	status: "sent",
	statusReason: "mock",
	provider: "mock",
	providerMessageId: "",
	providerStatus: "",
	attempts: 0,
	maxAttempts: 6,
	nextAttemptAt: null,
	lastError: "",
	createdAt: minutesAgo(5),
	updatedAt: minutesAgo(5),
	sentAt: minutesAgo(5),
	deliveredAt: null,
	...over,
})

const log: EmailLogEntry[] = [
	entry({ id: "eml_1" }),
	entry({
		id: "eml_2",
		template: "seller_email_code",
		templateTitle: "Код подтверждения почты",
		purpose: "seller_register_email",
		to: "new-seller@example.ru",
		subject: "Код подтверждения почты: ••••••",
		createdAt: minutesAgo(12),
	}),
	entry({
		id: "eml_3",
		template: "buyer_order_confirmed",
		templateTitle: "Подтверждение заказа (состав и сумма)",
		to: "anna@example.ru",
		subject: "Заказ №D1A3F09B оформлен и оплачен",
		status: "failed",
		statusReason: "550",
		lastError: "550 5.7.1 No such user",
		attempts: 1,
		createdAt: minutesAgo(40),
	}),
	entry({
		id: "eml_4",
		template: "buyer_price_drop",
		templateTitle: "Снижение цены на избранное",
		category: "marketing",
		to: "ivan@example.ru",
		subject: "Цена снижена: Платье миди из льна",
		status: "suppressed",
		statusReason: "unsubscribed",
		sentAt: null,
		createdAt: minutesAgo(90),
	}),
]

const previewHtml = (title: string) =>
	`<!DOCTYPE html><html lang="ru"><body style="margin:0;background:#F8F8F8;font-family:Arial,sans-serif;"><div style="max-width:600px;margin:24px auto;"><div style="color:#FA5C2E;font-weight:bold;font-size:22px;padding:0 8px 16px;">OSMIX</div><div style="background:#fff;border:1px solid #E2E2E2;border-radius:16px;padding:32px 28px;color:#1F1F1F;"><h1 style="font-size:22px;margin:0 0 16px;">${title}</h1><p>Предпросмотр в офлайн-режиме. Настоящая вёрстка письма собирается на шлюзе.</p></div></div></body></html>`

export const email: Api["email"] = {
	async status() {
		await sleep(120)
		return {
			provider: "mock",
			live: false,
			mode: "mock",
			missingConfig: [],
			configErrors: [],
			dailyLimit: 0,
			sentLast24h: 0,
			pausedUntil: null,
			pauseReason: "",
			from: "",
			fromName: "OSMIX",
			replyTo: "",
			smtpHost: "",
			smtpPort: 0,
			apiUrl: "",
			webhookUrl: "",
			variables: [
				{ name: "EMAIL_PROVIDER", set: true, secret: false },
				{ name: "EMAIL_FROM", set: false, secret: false },
				{ name: "EMAIL_FROM_NAME", set: true, secret: false },
				{ name: "EMAIL_REPLY_TO", set: false, secret: false },
			],
			note: "Почта не подключена: письма пишутся только в журнал (режим mock)",
			stats: { windowHours: 24, total: 4, sent: 2, delivered: 0, failed: 1, queued: 0, skipped: 1 },
		}
	},
	async log(filter = {}) {
		await sleep(120)
		const items = log.filter(
			(e) =>
				(!filter.to || e.to.includes(filter.to)) &&
				(!filter.status || filter.status === "all" || e.status === filter.status) &&
				(!filter.template || filter.template === "all" || e.template === filter.template),
		)
		return { items, total: items.length }
	},
	async message(id) {
		await sleep(80)
		const found = log.find((e) => e.id === id) ?? log[0]!
		return { ...found, html: previewHtml(found.subject), text: found.subject }
	},
	async retry(id) {
		await sleep(200)
		return { ok: true, id, status: "sent", error: "" }
	},
	async test() {
		await sleep(200)
		return { ok: true, id: "eml_test", status: "sent", error: "" }
	},
	async resume() {
		await sleep(80)
	},
	async templates() {
		await sleep(80)
		return templates
	},
	async preview(id) {
		await sleep(80)
		const meta = templates.find((t) => t.id === id) ?? templates[0]!
		return { ...meta, subject: meta.title, html: previewHtml(meta.title), text: meta.title }
	},
}
